import { useCallback, useEffect, useMemo } from "react";

import { useTeamMembers } from "@/features/agents/useTeamMembers";
import { useOptionalUserProfiles } from "@/features/userProfiles/context/UserProfilesContext";
import type {
  FieldSourceItem,
  FieldSourceUser,
  ResolveFieldSourceUser
} from "../components/fieldSource/types";
import type { CasePrepUscisFieldProvenance } from "../types";

/** Every actor uid the items' provenance names, the replaced entries' included. */
export function fieldSourceActorIds(items: FieldSourceItem[]): string[] {
  const ids = new Set<string>();
  const add = (
    entry: Partial<CasePrepUscisFieldProvenance> | null | undefined
  ) => {
    for (const uid of [entry?.filled_by_user_id, entry?.edited_by_user_id]) {
      const id = String(uid || "").trim();
      if (id) ids.add(id);
    }
  };
  for (const item of items) {
    add(item.provenance);
    add(item.provenance?.previous);
  }
  return [...ids].sort();
}

/**
 * Names and avatars for the users a form's provenance knows only by uid: the
 * person the case agent filled for, and fills recorded before names were
 * stored. Firm members give the name; the shared profile cache the avatar.
 */
export function useFieldSourceUsers(
  items: FieldSourceItem[]
): ResolveFieldSourceUser {
  const { members, me } = useTeamMembers();
  const profiles = useOptionalUserProfiles();
  const ensureProfiles = profiles?.ensure;
  const getProfile = profiles?.get;
  const actorIds = useMemo(() => fieldSourceActorIds(items), [items]);
  const actorKey = actorIds.join("\n");

  useEffect(() => {
    if (ensureProfiles && actorIds.length) void ensureProfiles(actorIds);
    // Only a new set of actors needs fetching.
  }, [actorKey, ensureProfiles]);

  const membersById = useMemo(
    () => new Map(members.map((member) => [member.uid, member])),
    [members]
  );

  return useCallback(
    (uid: string): FieldSourceUser | null => {
      const profile = getProfile?.(uid);
      const profileName = [profile?.first_name, profile?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (uid && uid === me.uid) {
        return {
          displayName: me.name || profileName || null,
          email: me.email || profile?.email || null,
          avatarUrl: me.avatarUrl || profile?.avatar_data_url || null
        };
      }
      const member = membersById.get(uid);
      if (!member && !profile) return null;
      return {
        displayName: member?.label || profileName || null,
        email: member?.email || profile?.email || null,
        avatarUrl: profile?.avatar_data_url || null
      };
    },
    [getProfile, me, membersById]
  );
}
